import {
  CtrlUser,
  CtrlUserAuthorities,
  DealPartner,
  Portfolio,
  RoleJava,
  UserManagementEntityType,
  UserType,
  UserWithRoles,
  UserWithRolesPayload
} from '@/app/shared/_models/models';
import {
  buildUserPayload,
  buildUsersFilter,
  subtreeAccountIds,
  UserFormValue
} from '@/app/shared/_utils/user-management.utils';
import { ApiService } from '@/app/shared/_services/api.service';
import { AuthenticationService } from '@/app/auth/_services/authentication.service';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { clone, pullAt, sortBy, startCase } from 'lodash';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzTabSetComponent } from 'ng-zorro-antd/tabs';
import { forkJoin, of, Subject, throwError } from 'rxjs';
import { catchError, map, takeUntil, tap, finalize } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.less']
})
export class UserManagementComponent implements OnChanges, OnDestroy {

  @ViewChild('usersTabs') usersTabs: NzTabSetComponent;

  @Input() entity: any;
  @Input() entityType: UserManagementEntityType;
  @Input() entityForm: UntypedFormGroup;
  /** Accounts in scope: the partner plus every agency beneath it. Also scopes the user list. */
  @Input() accountOptions: DealPartner[] = [];
  @Input() portfolioOptions: Portfolio[] = [];
  @Input() roleOptions: RoleJava[] = [];
  users: CtrlUserAuthorities[] = [];
  selectedAccountId: number | null = null;
  entityName: string;
  areUsersLoading = false;
  selectedUserIndex = 0;
  unsubscribe$ = new Subject<void>();
  readonly CURATED_MARKETPLACE_PARTNER_PREFIX = 'partner_';
  readonly NEXXEN_API_DOMAIN = '@NexxenAPI';

  constructor(
    private api: ApiService,
    private notification: NzNotificationService,
    private auth: AuthenticationService,
  ) { }

  get isPartner(): boolean {
    return this.entityType === UserManagementEntityType.EXCHANGE_PARTNER;
  }

  get isPublisher(): boolean {
    return this.entityType === UserManagementEntityType.PUBLISHER;
  }

  get canGenerateApiUser(): boolean {
    return !this.isPartner || this.auth.currentUserValue?.type === 'INTERNAL';
  }

  /** Only a parent has more than one agency to choose from. */
  get hasAccountFilter(): boolean {
    return this.isPartner && this.accountOptions.length > 1;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.entity?.currentValue) {
      this.selectedAccountId = Number(this.entity?.id) || null;
    }
    if (changes.entity?.currentValue || changes.accountOptions) {
      this.getAllUserContacts();
    }
  }

  onAccountFilterChange(): void {
    this.getAllUserContacts();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getAllUserContacts() {
    this.users = [];
    this.entityName = this.isPublisher ? 'Publisher' : 'Partner';
    this.selectedUserIndex = 0;
    this.areUsersLoading = true;
    this.fetchUsers()
      .pipe(
        takeUntil(this.unsubscribe$),
        catchError(() => {
          this.notification.error('Error', 'Failed to load users');
          return of([]);
        }),
        finalize(() => this.areUsersLoading = false)
      )
      .subscribe(users => {
        this.users = sortBy(users, [
          (a: CtrlUser) => a.type === 'API' ? 1 : 0,
          (a: CtrlUser) => `${a.firstName ?? ''} ${a.lastName ?? ''}`.toLowerCase(),
        ]);
      });
  }

  private fetchUsers(): Observable<CtrlUserAuthorities[]> {
    return this.isPartner ? this.fetchPartnerUsers() : this.fetchPublisherUsers();
  }

  private fetchPublisherUsers(): Observable<CtrlUserAuthorities[]> {
    return this.api.getUsers(buildUsersFilter('publisherId', 'EQUALS', this.entity?.publisherId))
      .pipe(map((res: { data?: CtrlUser[] }) => res.data ?? []));
  }

  private fetchPartnerUsers(): Observable<CtrlUserAuthorities[]> {
    const accountIds = this.selectedAccountId
      ? [this.selectedAccountId]
      : subtreeAccountIds(this.accountOptions, Number(this.entity?.id));
    if (!accountIds.length) {
      return of([]);
    }
    return this.api.getUsersWithRoles(buildUsersFilter('dealPartnerId', 'IN', accountIds))
      .pipe(map(res => (res.data ?? []).map(entry => this.toUserWithScope(entry))));
  }

  private toUserWithScope(entry: UserWithRoles): CtrlUserAuthorities {
    return {
      ...entry.user,
      roles: (entry.roles ?? []).map(role => role.name),
      portfolioIds: entry.portfolioIds ?? []
    };
  }

  addUser(type: UserType = 'EXTERNAL') {
    const user = { type } as CtrlUser;
    if (type === 'API') {
      const apiEmailPrefix = this.isPublisher
        ? this.entity?.publisherId
        : `${this.CURATED_MARKETPLACE_PARTNER_PREFIX}${this.entity?.id}`;
      user.email = `${apiEmailPrefix}${this.NEXXEN_API_DOMAIN}`;
      user.firstName = 'API';
      user.lastName = 'User';
    }
    this.users.push(user);
    this.selectedUserIndex = this.users.length - 1;
  }

  onRemoveUnsavedUser(i) {
    pullAt(this.users, i);
    if (this.usersTabs.nzSelectedIndex === i) {
      this.selectedUserIndex = this.users.length - 1;
    } else if (this.usersTabs.nzSelectedIndex > i) {
      this.selectedUserIndex--;
    }
  }

  toggleUserDeleteStatus(i) {
    this.users[i].toBeRemoved = !this.users[i].toBeRemoved;
  }

  onUserFormChange(event, user: CtrlUser) {
    user.form = event;
  }

  isAnyUserFormInvalid() {
    let isInvalid = false;
    this.users.forEach((user: CtrlUser) => {
      if (user.toBeRemoved !== true && user.form) {
        user.form.markAllAsTouched();
        if (user.form.invalid) {
          isInvalid = true;
        }
      }
    });
    return isInvalid;
  }

  isDirty(): boolean {
    return this.users.some(user => user.form?.dirty || user.toBeRemoved || user.id === undefined);
  }

  reload(): void {
    this.getAllUserContacts();
  }

  numberOfRemainingUsers() {
    return this.users.filter(user => {
      if (user.id === undefined && user.form === undefined) {
        return;
      }
      if (!user.toBeRemoved) {
        return user;
      }
    }).length;
  }

  executeUserOperations() {
    const add = [];
    const update = [];
    const remove = [];

    // separate users to different actions
    this.users.forEach(user => {
      if (user.toBeRemoved) {
        remove.push(clone(user));
      } else {
        if (user.id && user.form) {
          update.push(clone(user));
        } else if (user.id === undefined && user.form) {
          add.push(clone(user));
        }
      }
    });

    // Convert individual operations to observables
    const addOperations$ = add.map((user: CtrlUser) => {
      const payload = this.buildPayload(user.form.getRawValue());
      payload.user = {
        ...payload.user,
        type: user.type,
        publisherId: this.isPublisher ? this.entity.publisherId : null,
      };
      return this.api.createNewUser(payload);
    });

    const updateOperations$ = update.map((user: CtrlUser) => {
      const payload = this.buildPayload(user.form.value);
      if (payload.user.password === null) {
        delete payload.user.password;
        delete payload.user.passwordConfirmation;
      }
      return this.api.updateUserCredential(payload, user.id, 'INTERNAL');
    });

    const removeOperations$ = remove.map((user: CtrlUser) =>
      this.api.deleteUser(user.id)
    );

    return forkJoin([
      addOperations$.length ? forkJoin(addOperations$) : of([]),
      updateOperations$.length ? forkJoin(updateOperations$) : of([]),
      removeOperations$.length ? forkJoin(removeOperations$) : of([])
    ]).pipe(
      catchError(err => {
        if (err?.error) {
          this.notification.error('Error', err.error.message);
        }
        return throwError(() => err);
      })
    );
  }

  saveAllUserChangesAsPromise(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.executeUserOperations()
        .pipe(tap(() => this.getAllUserContacts()))
        .subscribe({
          next: () => resolve(),
          error: (err) => reject(err)
        });
    });
  }

  saveAllUserChanges(): Observable<[any[], any[], any[]]> {
    return this.executeUserOperations();
  }

  toStartCase(str: string) {
    return startCase(str);
  }

  getUserName(user) {
    if (user.type === 'API') {
      return 'API User';
    }
    if (user.id > 0) {
      return`${user.firstName} ${user.lastName}`;
    }
    return 'New User';
  }

  hasApiUser() {
    return this.users.filter(u => u.type === 'API').length ? true : false;
  }

  private buildPayload(formValue: UserFormValue): UserWithRolesPayload {
    return buildUserPayload(formValue, this.isPartner, {
      roleOptions: this.roleOptions,
      partnerId: Number(this.entity?.id)
    });
  }

}
